import { useState, FormEvent, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSharedState } from "state";
import ModalComponent from "components/modal";
import { addCode, updateCode } from "api/code";

type FormErrors = Record<string, boolean>;

type FormRecords = {
  code_it: string;
};

const REQUIRED_FIELDS = ["code_it"] as const;

export default function NewCode() {
  const { codes, getCodesData, notify } = useSharedState();
  const { codeId } = useParams();
  const navigate = useNavigate();

  const codeItem = useMemo(() => {
    return codes.find((t) => t.code_it_id === Number(codeId));
  }, [codes]);

  const [formRecords, setFormRecords] = useState<FormRecords>({
    code_it: "",
  });

  useEffect(() => {
    if (codeId) {
      if (codeItem) {
        setFormRecords({
          code_it: codeItem.code_it,
        });
      } else {
        notify("Код ИТ не найден", "danger");
        navigate("/codes");
      }
    }
  }, [codeId, codeItem]);

  const [errors, setErrors] = useState<FormErrors>({});

  const [isFormLoading, setFormLoading] = useState<boolean>(false);
  const [isFormSubmitted, setFormSubmitted] = useState<boolean>(false);

  const codeFields = [
    {
      id: "code_it",
      title: "Наименование кода ИТ",
      type: "text",
      placeholder: "Введите наименование",
    },
  ] as const;

  const validateForm = () => {
    const newErrors: FormErrors = {};
    REQUIRED_FIELDS.forEach((field) => {
      const value = formRecords[field];
      if (Array.isArray(value) ? value.length === 0 : !value?.toString().trim()) {
        newErrors[field] = true;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormRecords((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: false }));
  };

  const submitCode = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setFormLoading(true);
    try {
      if (codeId) {
        await updateCode(Number(codeId), formRecords.code_it, true, codeItem?.status);
        notify("Код ИТ обновлен", "success");
      } else {
        await addCode(formRecords.code_it);
        notify("Код ИТ создан", "success");
      }

      setFormRecords({ code_it: "" });
      getCodesData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", codeId ? "Ошибка при обновлении кода ИТ" : "Ошибка при создании кода ИТ");
    } finally {
      setFormLoading(false);
    }
  };

  const renderField = ({ id, title, placeholder }: (typeof codeFields)[number]) => {
    return (
      <div className={"col-lg-6"} key={id}>
        <div className="mb-3">
          <label className="form-label required">{title}</label>
          <div className="input-icon mb-3">
            <input
              type="text"
              className={`form-control ${errors[id] ? "is-invalid" : ""}`}
              name={id}
              placeholder={placeholder}
              value={(formRecords[id] as string) || ""}
              onChange={handleInputChange}
              disabled={isFormLoading}
            />

            <div className="invalid-feedback">Введите корректное значение</div>
            {isFormLoading && (
              <span className="input-icon-addon">
                <div className="spinner-border spinner-border-sm text-secondary" role="status" />
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const newCodeBody = (
    <div>
      <form id="codeForm" className="row" onSubmit={submitCode}>
        {codeFields.map(renderField)}
      </form>
    </div>
  );

  const newCodeFooter = (
    <button type="submit" form="codeForm" className="btn btn-success d-flex gap-1" disabled={isFormLoading}>
      {codeId ? "Сохранить изменения" : "Создать код ИТ"}
      {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
    </button>
  );

  return (
    <ModalComponent
      header={codeId ? "Редактирование кода ИТ" : "Новый код ИТ"}
      body={newCodeBody}
      footer={newCodeFooter}
      disableClose={isFormLoading}
      shouldModalClose={isFormSubmitted}
    />
  );
}
